import type { ReactNode } from 'react'

import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'

import { useGsap } from '@/hooks/use-gsap'
import { SplitWords } from '@/components/generals/split-words'
import { AccordionList } from '@/components/generals/accordion-list'
import { homeFaqs } from '@/constants/home'

export const Faq = (): ReactNode => {
    const scope = useGsap<HTMLElement>(({ gsap, select, scope: root, reduced }) => {
        if (reduced) return

        gsap.from(select('[data-faq-heading] .reveal-mask > span'), {
            yPercent: 115,
            duration: 1,
            stagger: 0.05,
            ease: 'nuvea',
            scrollTrigger: { trigger: root, start: 'top 76%' }
        })

        gsap.from(select('[data-faq-cta]'), {
            y: 18,
            opacity: 0,
            duration: 0.85,
            delay: 0.15,
            ease: 'nuvea',
            scrollTrigger: { trigger: root, start: 'top 76%' }
        })

        // Rows peel in from the top of the stack.
        gsap.from(select('[data-accordion-row]'), {
            y: 26,
            opacity: 0,
            duration: 0.8,
            stagger: 0.06,
            ease: 'nuvea',
            scrollTrigger: {
                trigger: select('[data-faq-list]')[0],
                start: 'top 84%'
            }
        })
    })

    return (
        <section
            ref={scope}
            id='faq'
            className='shell-x mx-auto w-full max-w-[1760px] py-24 md:py-32'
        >
            <div className='grid grid-cols-1 gap-10 lg:grid-cols-[1fr_1.3fr] lg:gap-16'>
                <div className='lg:sticky lg:top-28 lg:self-start'>
                    <SplitWords
                        data-faq-heading=''
                        as='h2'
                        lines={['Questions,', 'answered plainly.']}
                        className='type-h1 max-w-[12ch] text-ink'
                    />

                    <Link
                        data-faq-cta=''
                        href='/support'
                        className='type-small group mt-8 inline-flex items-center gap-2 rounded-full border border-stone-line px-5 py-3 text-ink transition-[background-color,border-color,color] duration-400 ease-nuvea hover:border-ink hover:bg-ink hover:text-cream'
                    >
                        Contact Support

                        <ArrowUpRight
                            className='size-4 transition-transform duration-400 ease-nuvea group-hover:translate-x-0.5 group-hover:-translate-y-0.5'
                            strokeWidth={1.6}
                        />
                    </Link>
                </div>

                <div data-faq-list=''>
                    <AccordionList items={homeFaqs} defaultOpen={0} />
                </div>
            </div>
        </section>
    )
}
